import type { TFunction } from 'i18next'

// Shapes of the API error envelope produced by the backend.
interface NewEnvelopeEntry {
  code: string
  message: string
  params?: Record<string, unknown>
  field?: string
}

interface NewEnvelope {
  code: string
  message: string
  params?: Record<string, unknown>
}

interface BatchEnvelope {
  errors: NewEnvelopeEntry[]
}

interface LegacyEnvelope {
  detail: string
}

type ResponseData = NewEnvelope | BatchEnvelope | LegacyEnvelope | unknown

const AXIOS_STATUS_MESSAGE = /^Request failed with status code (\d+)$/

function translateEntry(entry: NewEnvelopeEntry, t: TFunction): string {
  return t(`errors:${entry.code}`, {
    defaultValue: entry.message,
    ...entry.params,
  })
}

function reauthFlagFromUnknown(value: unknown): boolean {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'needs_reauth' in value &&
      (value as { needs_reauth?: unknown }).needs_reauth === true,
  )
}

/** Allegro/eMAG token expired — HTTP 401 with needs_reauth, not an Ofertator session expiry. */
export function isReauthApiError(error: unknown): boolean {
  const data = extractResponseData(error)
  if (reauthFlagFromUnknown(data)) return true
  if (data !== null && typeof data === 'object' && 'detail' in data) {
    return reauthFlagFromUnknown((data as { detail: unknown }).detail)
  }
  return false
}

function extractResponseData(error: unknown): ResponseData | null {
  if (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response?: { data?: unknown } }).response?.data !== undefined
  ) {
    return (error as { response: { data: ResponseData } }).response.data
  }
  return null
}

export function getApiErrorStatus(error: unknown): number | undefined {
  if (error === null || typeof error !== 'object' || !('response' in error)) {
    return undefined
  }
  const status = (error as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * Deploy maintenance body from nginx (`/__maintenance_api__`):
 * `{ "error": "maintenance", "message": "..." }` — possibly as a raw string
 * when Content-Type is not JSON.
 */
export function isMaintenancePayload(data: unknown): boolean {
  if (typeof data === 'string') {
    return /"error"\s*:\s*"maintenance"/.test(data)
  }
  if (data && typeof data === 'object' && 'error' in data) {
    return (data as { error?: unknown }).error === 'maintenance'
  }
  return false
}

/** HTTP 503 with the nginx maintenance payload — used by the response interceptor. */
export function isMaintenanceApiError(error: unknown): boolean {
  return getApiErrorStatus(error) === 503 && isMaintenancePayload(extractResponseData(error))
}

function serviceUnavailableMessage(t: TFunction): string {
  // Prefer the deploy banner copy when i18n has it; fall back to errors catalog.
  const maintenance = t('common:maintenance.message', { defaultValue: '' })
  if (maintenance.trim()) return maintenance
  return t('errors:server.unavailable')
}

/**
 * Translate an API error into a user-facing string using the i18next `t` function.
 *
 * Handles:
 *  - Deploy maintenance `{ error: "maintenance" }` / HTTP 503 without envelope
 *  - New envelope  `{ code, message, params? }`          → i18n key `errors:<code>`
 *  - Batch envelope `{ errors: [{ code, message, ... }] }` → joined entries
 *  - Legacy         `{ detail: "..." }`                   → raw detail string
 *  - Plain JS Error                                       → error.message
 *  - Anything else                                        → t('errors:unknown')
 */
export function translateApiError(error: unknown, t: TFunction): string {
  const data = extractResponseData(error)
  const status = getApiErrorStatus(error)

  // Nginx deploy window — ignore English `message` on the maintenance payload.
  if (isMaintenancePayload(data)) {
    return serviceUnavailableMessage(t)
  }

  if (data !== null && typeof data === 'object') {
    // Batch envelope: 422 with multiple field errors
    if ('errors' in data && Array.isArray((data as BatchEnvelope).errors)) {
      const entries = (data as BatchEnvelope).errors
      if (entries.length > 0) {
        return entries.map((e) => translateEntry(e, t)).join('\n')
      }
    }

    // New single-error envelope
    if ('code' in data && typeof (data as NewEnvelope).code === 'string') {
      return translateEntry(data as NewEnvelopeEntry, t)
    }

    // Legacy { detail: "..." } or { detail: { message, ... } } — pre-envelope endpoints
    if ('detail' in data) {
      const detail = (data as { detail: unknown }).detail
      if (typeof detail === 'string') {
        return detail
      }
      if (
        detail !== null &&
        typeof detail === 'object' &&
        'message' in detail &&
        typeof (detail as { message: unknown }).message === 'string'
      ) {
        return (detail as { message: string }).message
      }
    }
  }

  // Bare 503/502/504 (no app envelope) — never show Axios' raw status string.
  if (status === 503 || status === 502 || status === 504) {
    return serviceUnavailableMessage(t)
  }

  if (error instanceof Error) {
    const match = AXIOS_STATUS_MESSAGE.exec(error.message)
    if (match && (match[1] === '503' || match[1] === '502' || match[1] === '504')) {
      return serviceUnavailableMessage(t)
    }
    return error.message
  }

  return t('errors:unknown')
}
